import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faTimes } from '@fortawesome/free-solid-svg-icons';
import './SearchBar.css';
import Config from '../utils/Config'; // Import the Config file
import {
  fetchFromAPI,
} from '../utils/functions'; // Import utility functions
import useTitle from '../hooks/useTitle';

const HISTORY_KEY = 'searchHistory_v1';
const MAX_STORED = 20;   // how many to keep in localStorage
const MAX_VISIBLE = 8;   // how many to show in the dropdown
const SHOW_AFTER = window.matchMedia('(pointer: coarse)').matches ? 1 : 2;

const SearchBar = ({ onSearch, setCurrentPage, onClear }) => {
  const [query, setQuery] = useState('');
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState(''); // Default to "All States"
  const [history, setHistory] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchInputRef = useRef(null); // Reference for the search input
  const listboxRef = useRef(null);

  useTitle('Search by Name');

  useEffect(() => {
    onClear?.();      // nuke parent results when this search mounts
    setCurrentPage?.(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load states + history
  useEffect(() => {
    (async () => {
      try {
        const response = await fetchFromAPI(Config.ENDPOINTS.DEALERS_STATES, { method: 'GET' });
        setStates([{ name: 'All States', abbreviation: '' }, ...response.data]);
      } catch (err) {
        console.error('Error fetching states:', err);
      }
    })();

    const saved = localStorage.getItem(HISTORY_KEY);
    if (saved) {
      try { setHistory(JSON.parse(saved)); } catch { }
    }

    searchInputRef.current?.focus();
  }, []);

  // --- suggestions: return nothing when empty ---
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < SHOW_AFTER) return [];
    return history
      .filter(h => h.query.toLowerCase().includes(q))
      .slice(0, MAX_VISIBLE);
  }, [history, query]);

  const saveHistory = (entry) => {
    const key = `${entry.query}||${entry.state || ''}`;
    const next = [entry, ...history.filter(h => `${h.query}||${h.state || ''}` !== key)];
    const trimmed = next.slice(0, MAX_STORED);
    setHistory(trimmed);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(trimmed));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedQuery = query.trim();
    const trimmedState = selectedState.trim();
    if (trimmedQuery !== '' || trimmedState !== '') {
      onSearch({ query: trimmedQuery, state: trimmedState });
      saveHistory({ query: trimmedQuery, state: trimmedState });
      setOpen(false);
      setActiveIndex(-1);
      // keep query in the box; remove this line if you want to clear it:
      // setQuery('');
    }
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true);
      return;
    }
    if (!open && e.key === 'Enter') return handleSubmit(e);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % Math.max(1, suggestions.length));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
        break;
      case 'Enter':
        if (open && activeIndex >= 0 && suggestions[activeIndex]) {
          e.preventDefault();
          const s = suggestions[activeIndex];
          setQuery(s.query);
          setSelectedState(s.state || '');
          setOpen(false);
          setActiveIndex(-1);
          // submit immediately
          onSearch({ query: s.query, state: s.state || '' });
          saveHistory({ query: s.query, state: s.state || '' });
        } else {
          handleSubmit(e);
        }
        break;
      case 'Escape':
        setOpen(false);
        setActiveIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSelectSuggestion = (s) => {
    setQuery(s.query);
    setSelectedState(s.state || '');
    setOpen(false);
    setActiveIndex(-1);
    searchInputRef.current?.focus();
    onSearch({ query: s.query, state: s.state || '' });
    saveHistory({ query: s.query, state: s.state || '' });
  };

  // open dropdown on focus; close on outside click
  useEffect(() => {
    const onClickOutside = (e) => {
      if (
        !searchInputRef.current?.contains(e.target) &&
        !listboxRef.current?.contains(e.target)
      ) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <form className="search-bar" onSubmit={handleSubmit}>
      {/* Visible-to-screenreaders label (visually hidden) */}
      <label className="sr-only" htmlFor="name-query">Search by name</label>
      <div className="search-combobox">
        <div className="input-wrapper">
          <input
            id="name-query"
            type="text"
            placeholder="Enter search query..."
            value={query}
            onChange={(e) => {
              const val = e.target.value;
              setQuery(val);
              const hasQuery = val.trim().length >= SHOW_AFTER;
              setOpen(hasQuery);
              setActiveIndex(-1);
            }}
            onKeyDown={(e) => {
              const hasQuery = query.trim().length >= SHOW_AFTER;
              if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                if (hasQuery) setOpen(true);
                return;
              }
              if (!open && e.key === 'Enter') return handleSubmit(e);
            }}
            ref={searchInputRef}
            className="custom-autocomplete-input"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={open && suggestions.length > 0}
            aria-controls="name-suggestions"
            aria-activedescendant={activeIndex >= 0 ? `suggestion-${activeIndex}` : undefined}
            autoComplete="off"
          />

          {query && (
            <button
              type="button"
              className="clear-btn"
              onClick={() => {
                setQuery('');
                setSelectedState('');
                setOpen(false);
                setActiveIndex(-1);
                onClear?.();
                searchInputRef.current.focus();
              }}
              aria-label="Clear search"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>

        {/* Suggestions dropdown */}
        {open && suggestions.length > 0 && (
          <ul
            id="name-suggestions"
            className="autocomplete-list"
            role="listbox"
            ref={listboxRef}
          >
            {suggestions.map((s, idx) => (
              <li
                key={`${s.query}||${s.state || ''}||${idx}`}
                id={`suggestion-${idx}`}
                role="option"
                aria-selected={idx === activeIndex}
                className={`autocomplete-item ${idx === activeIndex ? 'active' : ''}`}
                // use mousedown to avoid losing focus before click handler fires
                onMouseDown={(e) => { e.preventDefault(); handleSelectSuggestion(s); }}
              >
                <span className="sugg-query">{s.query}</span>
                {s.state ? <span className="sugg-state">({s.state})</span> : null}
              </li>
            ))}
            <li className="autocomplete-footer">
              <button
                type="button"
                className="clear-history-btn"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { setHistory([]); localStorage.removeItem(HISTORY_KEY); setOpen(false); }}
              >
                ❌&nbsp;Clear recent searches
              </button>
            </li>
          </ul>
        )}
      </div>

      <label className="sr-only" htmlFor="state-select">State</label>
      <select
        id="state-select"
        value={selectedState}
        onChange={(e) => setSelectedState(e.target.value)}
        onKeyDown={handleKeyDown} // Allow "Enter" key for dropdown
        className="state-dropdown"
      >
        {states.map((state, index) => (
          <option key={`${state.abbreviation}-${index}`} value={state.abbreviation}>
            {state.name} {/* Full name inside the dropdown options */}
          </option>
        ))}
      </select>
      <button type="submit" className="search-button">
        <FontAwesomeIcon icon={faMagnifyingGlass} />
      </button>
    </form>
  );
};

export default SearchBar;
